import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { API_BASE, AUTH_AUDIENCE } from '../config/env';

type RequestInitWithAuth = RequestInit & { skipAuth?: boolean };

export function useApiClient() {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const getToken = useCallback(async () => {
    try {
      return await getAccessTokenSilently({ authorizationParams: { audience: AUTH_AUDIENCE } });
    } catch (err: any) {
      if (err?.error === 'login_required') {
        await loginWithRedirect({ appState: { returnTo: window.location.pathname } });
        return '';
      }
      throw err;
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const fetchApi = useCallback(
    async (path: string, init: RequestInitWithAuth = {}) => {
      const token = init.skipAuth ? '' : await getToken();
      return fetch(`${API_BASE}${path}`, {
        ...init,
        headers: {
          ...(init.headers || {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    },
    [getToken],
  );

  const fetchJson = useCallback(
    async <T>(path: string, init?: RequestInitWithAuth) => {
      const res = await fetchApi(path, init);
      if (!res.ok) throw new Error(`Request failed ${res.status}`);
      return (await res.json()) as T;
    },
    [fetchApi],
  );

  return { fetchApi, fetchJson, getToken };
}
